import { InfiniteFaqItem } from '../types/infiniteLanding.types'

export const INFINITE_FAQ_ITEMS: InfiniteFaqItem[] = [
  {
    question: 'Who is Infinite meant for?',
    answerHtml: `<p>Infinite is for investors who want their portfolio fully managed.</p>
<p>Typically, this includes:</p>
<ul>
  <li>Portfolios of ₹20L+</li>
  <li>Portfolios that are hard to manage or feel cluttered</li>
  <li>Inconsistent or underwhelming returns</li>
  <li>Lack of clarity around asset allocation and risk</li>
  <li>Moving away from regular funds to unbiased investment advisory</li>
  <li>Investing without a clear plan or defined goals (retirement, home purchase, children's education, weddings, etc.)</li>
</ul>
<p>Reasons can vary from person to person — but the bottom line is you get a professional you can trust to manage your portfolio, while you stay in control of your goals and long-term wealth creation.</p>`
  },
  {
    question: 'How is this different from PMS?',
    answerHtml: `<p>Unlike a PMS, Infinite doesn't follow a single strategy — it builds the right strategy for you.</p>
<p>With Infinite:</p>
<ul>
  <li>Your portfolio strategy is customised for you (not a model portfolio)</li>
  <li>You get a dedicated team of experts at your disposal</li>
  <li>Your money stays fully in your control</li>
  <li>No Power of Attorney (POA) required</li>
  <li>Every transaction needs your approval</li>
  <li>End-to-end portfolio management, SIPs, and goal planning are included</li>
</ul>`
  },
  {
    question: 'Why not just manage it myself?',
    answerHtml: `<p>You absolutely can — and many investors start that way.</p>
<p>In the early stages, managing your own portfolio feels straightforward. You have fewer funds, decisions are simpler, and the stakes don't feel as high.</p>
<p>But as your portfolio grows, things begin to change:</p>
<ul>
  <li>Decisions become more nuanced and harder to get right</li>
  <li>Rebalancing gets delayed, even when you know it should be done</li>
  <li>Tax implications become more complex</li>
  <li>Market noise and emotions start influencing decisions more than they should</li>
</ul>
<p>Infinite is built for this stage — where timely decisions and consistent execution start to matter far more than just knowing what to do.</p>
<p>In investing, what hurts the most is not always the wrong decision — it's the delay in taking action.</p>
<p>Missed rebalancing, postponed moves, and waiting for the “right time” quietly compound into the biggest cost of lost opportunity over time.</p>`
  },
  {
    question: 'What is the minimum investment?',
    answerHtml: `<p>Infinite works best for portfolios of ₹20L+, along with SIPs of ₹50K+ per month.</p>
<p>That said, we do make exceptions for:</p>
<ul>
  <li>High SIPs (₹1L+ monthly)</li>
  <li>Fast-growing portfolios that are getting complex</li>
</ul>`
  },
  {
    question: 'Can I customise my portfolio strategy?',
    answerHtml: `<p>100%. That's the whole purpose of Infinite — to build the right portfolio for you.</p>
<p>While building your portfolio strategy, we consider:</p>
<ul>
  <li>Your existing portfolio and holdings</li>
  <li>Your risk appetite, life stage, goals, liabilities — everything that matters</li>
  <li>Your asset allocation preferences</li>
</ul>
<p>Our goal is to build the right portfolio for you — incorporating your preferences while ensuring it is managed with discipline and consistency, aligned to your goals and long-term wealth creation.</p>`
  },
  {
    question: 'How do buy and sell transactions work?',
    answerHtml: `<p>All transactions are executed directly from your own account — ensuring you remain fully in control of your money at every step.</p>
<ul>
  <li>We recommend what actions to take (buy, sell, rebalance)</li>
  <li>You review and approve each transaction</li>
  <li>Once approved, transactions are executed directly from your bank account</li>
</ul>
<p>There is no need for a Power of Attorney (POA), and your money never moves without your approval — you remain in control of every transaction.</p>
<p>As a SEBI-registered RIA, we are not permitted to come in the middle of your transactions, which ensures complete transparency and alignment.</p>`
  },
  {
    question: 'How do I track what is happening?',
    answerHtml: `<p>Transparency is core to how we build at PowerUp — you always have full visibility into your portfolio and every decision we make.</p>
<p>You always get:</p>
<ul>
  <li>Real-time tracking of your portfolio through the PowerUp app</li>
  <li>Clear rationale behind every recommendation</li>
  <li>Ongoing performance monitoring and discussions via your Infinite Circle</li>
  <li>Regular updates and reviews from our team of experts</li>
</ul>
<p>Nothing is a black box — you always know what's happening, why it's happening, and how it impacts your portfolio.</p>`
  },
  {
    question: 'Will I have a dedicated point of contact?',
    answerHtml: `<p>Yes. We've designed the Infinite Circle experience so you always have a dedicated team of experts at your disposal.</p>
<p>With your Infinite Circle, you get:</p>
<ul>
  <li>A private WhatsApp group for real-time support</li>
  <li>A dedicated Circle Manager who understands your portfolio and goals</li>
  <li>Access to both our investment team (mutual fund experts) and servicing team (Circle Assistant)</li>
</ul>
<p>You're never on your own — our team is always available to guide you and help you take the right actions at the right time.</p>`
  },
  {
    question: 'Is my money safe?',
    answerHtml: `<p>Yes — your money always remains in your own name and under your full control, at all times.</p>
<ul>
  <li>All investments are held directly with the respective AMC / RTA</li>
  <li>Your money is never pooled into any common account</li>
  <li>We do not take custody of your funds at any point</li>
</ul>
<p>There is no movement of funds without your approval — you remain in control of your investments at all times.</p>
<p>As a SEBI-registered RIA, we are not permitted to hold or handle your money, which ensures complete transparency and eliminates any conflict of interest.</p>`
  },
  {
    question: 'Can I withdraw my money anytime?',
    answerHtml: `<p>Absolutely — investing via Infinite is the same as investing in mutual funds from any platform.</p>
<p>Since everything is held in your own name, you can withdraw or redeem your money anytime, as per the lock-in rules of the respective AMCs and schemes.</p>
<p>There are no lock-ins imposed by us at any point — you remain fully in control of when and how you access your funds.</p>`
  },
  {
    question: 'What does RIA mean?',
    answerHtml: `<p>RIA stands for Registered Investment Advisor — a license issued by Securities and Exchange Board of India to individuals and firms who provide investment advice.</p>
<p>What this means for you:</p>
<ul>
  <li>We are legally bound to act in your best interest</li>
  <li>We do not earn commissions from any products</li>
  <li>Our advice is completely unbiased and transparent</li>
</ul>
<p>RIAs operate under strict regulatory guidelines to ensure investor protection and alignment.</p>
<p>Simply put, we always work in your best interest and we earn only when you pay us, not through commissions — so our incentives remain fully aligned with your outcomes.</p>`
  },
  {
    question: 'How does pricing work?',
    answerHtml: `<p>We charge a transparent fee based on the size of your portfolio (AUM) — this is the most standard way professional investment advisory services are structured.</p>
<p>Most investment advisory services in India typically charge ~1%-1.5% per year, often with limited visibility.</p>
<p>With Infinite, we've reimagined this — offering high-quality, end-to-end portfolio management at a significantly lower and fully transparent cost.</p>
<p>This is a special launch price for Elite members only.</p>
<p>Launch pricing:</p>
<ul>
  <li>₹20L - ₹50L → <s>1.00%</s> 0.75% p.a + GST</li>
  <li>₹50L - ₹1Cr → <s>0.75%</s> 0.625% p.a + GST</li>
  <li>₹1Cr+ → <s>0.625%</s> 0.50% p.a + GST</li>
</ul>
<p>This fee is charged quarterly, based on the average AUM managed over the period.</p>`
  },
  {
    question: 'Do you guarantee returns?',
    answerHtml: `<p>Absolutely not. We don't guarantee returns — and no one can.</p>
<p>We built PowerUp to make high-quality investment advisory accessible for every mutual fund investor.</p>
<p>All our recommendations are backed by:</p>
<ul>
  <li>20+ years of data</li>
  <li>Backtested frameworks</li>
  <li>Expert curation by our in-house team</li>
</ul>
<p>As a SEBI-registered RIA, we always work in your best interest — and we earn only when you pay us, not through commissions, so our incentives remain fully aligned with your outcomes.</p>`
  },
  {
    question: 'Why is there a waitlist?',
    answerHtml: `<p>Infinite is limited by our capacity to serve our members well.</p>
<p>We onboard a limited number of clients at a time so we can:</p>
<ul>
  <li>Give adequate attention to each member</li>
  <li>Build the right portfolio aligned to your goals</li>
  <li>Stay proactive with monitoring and execution</li>
  <li>Maintain a high quality of service</li>
</ul>
<p>By joining the waitlist, you reserve your place with us.</p>
<p>Joining the waitlist simply means we'll reach out when we're ready — and you can take a call then.</p>`
  },
  {
    question: 'What happens after I join the waitlist?',
    answerHtml: `<p>Once we're ready to onboard you:</p>
<ul>
  <li>We reach out and walk you through everything</li>
  <li>Understand your portfolio, goals, and current setup</li>
  <li>Review your existing investments</li>
  <li>Share a tailored strategy and approach</li>
</ul>
<p>At that point, you can decide if you'd like to proceed.</p>
<p>There's no commitment or upfront payment — you take a call once you have full clarity.</p>`
  }
]
